import React, { useMemo } from 'react';

import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { PrintableContainer } from '@components/Printable';
import { TrackingTable } from '@components/utilities/CargoTracking/CargoTrackingResult';
import { useTrackingDataList } from '@hooks/queries/useTrackingDataList';
import { useSearch } from '@hooks/useSearch';
import { TrackingDataHelper } from '@models/TrackingData';
import { printCargoTrackingDetails } from '@pages/utilities/CargoTracking/helpers';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { toJST, toJSTDate } from '@utils/types';
import clsx from 'clsx';
import { pipe } from 'remeda';

const strings = createStrings({
  ja: {
    title: '配送情報詳細',
    print: '印刷',
    d2dNo: 'D2D番号',
    date: '日付',
    transportStatus: '輸送状況',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  buttonContainer: {
    ...common.flexCenter,
    marginTop: 72,
    '@media print': {
      display: 'none',
    },
  },
  infoTable: {
    ...common.middleText,
    ...common.textGray700,
    '& th': {
      ...common.fontWeight500,
      ...common.textGray800,
      textAlign: 'left',
    },
  },
}));

export const PrintCargoTrackingDetails: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    search: { trackNo },
  } = useSearch(printCargoTrackingDetails);

  const dateString = useMemo(() => toJST(new Date()), []);

  const trackingList = useTrackingDataList([trackNo]);

  const item = trackingList.data?.[0];

  return (
    <Layout
      title={strings.title}
      hideHeader
      hideFooter
      hideCookiePolicy
      loading={trackingList.isLoading}
      errors={[trackingList.error]}
      classes={{
        background: classes.background,
      }}
    >
      {() => (
        <PrintableContainer>
          <div className={clsx(classes.flexInline, classes.middleText, classes.textGray800)}>
            {strings.title}
            <span style={{ width: '2rem' }} />
            {dateString}
          </div>

          <div style={{ height: 30 }} />

          <table className={clsx(classes.infoTable)}>
            <tr>
              <th>{strings.d2dNo}</th>
              <th>{strings.date}</th>
              <th>{strings.transportStatus}</th>
            </tr>
            <tr>
              <td>{trackNo}</td>
              <td>
                {pipe(
                  new TrackingDataHelper(item!).getLastVisibleInCargoTrackingTransportStatus()?.[1]
                    ?.time,
                  (t) => (t ? toJSTDate(new Date(t)) : '-'),
                )}
              </td>
              <td>{item!.transport_status}</td>
            </tr>
          </table>

          <div style={{ height: 50 }} />

          <TrackingTable variant="printable" trackingData={item!} />

          <div className={classes.buttonContainer}>
            <Button color="printable" text={strings.print} size="small" onClick={window.print} />
          </div>
        </PrintableContainer>
      )}
    </Layout>
  );
};
